import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Switch,
  Box,
} from "@mui/material";
import * as Yup from "yup";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

export interface ServiceChargeCreate {
  content_type_id: number | null;
  external_service_resource_id: number | null;
  feature_id: number | null;
  action_event: string;
  is_credit_charge: boolean;
  credit_charge: number;
  unit_type: "ACTION" | "TIME";
  unit: "SECOND" | "MINUTE" | "HOUR" | "DAY" | "MONTH" | "ANNUAL" | null;
  unit_value: number | null;
  status: "ACTIVE" | "UPCOMING";
  negative_deduction_allowed: boolean;
}

interface CreateServiceChargeFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: ServiceChargeCreate) => void;
}

const validationSchema = Yup.object()
  .shape({
    action_event: Yup.string().required("Action event is required"),
    feature_id: Yup.number().nullable().required("Feature ID is required"),
    content_type_id: Yup.number().nullable(),
    external_service_resource_id: Yup.number().nullable(),
    is_credit_charge: Yup.boolean().required("Credit charge flag is required"),
    credit_charge: Yup.number()
      .required("Credit charge is required")
      .min(0, "Must be at least 0"),
    unit_type: Yup.mixed<"ACTION" | "TIME">()
      .oneOf(["ACTION", "TIME"], "Invalid unit type")
      .required("Unit type is required"),
    unit: Yup.string().nullable(),
    unit_value: Yup.number().nullable(),
    status: Yup.mixed<"ACTIVE" | "UPCOMING">()
      .oneOf(["ACTIVE", "UPCOMING"], "Invalid status")
      .required("Status is required"),
    negative_deduction_allowed: Yup.boolean().required(),
  })
  .test(
    "contentOrExternal",
    "Either content_type_id or external_service_resource_id must be provided, but not both",
    function (value) {
      const ct = value?.content_type_id;
      const es = value?.external_service_resource_id;
      const hasCt = ct != null;
      const hasEs = es != null;
      return (hasCt || hasEs) && !(hasCt && hasEs);
    }
  )
  .test(
    "unitTimeFields",
    "Unit and unit_value are required when unit_type is TIME",
    function (value) {
      if (value?.unit_type === "TIME") {
        return value.unit != null && value.unit_value != null;
      }
      return true;
    }
  );

const CreateServiceCharge: React.FC<CreateServiceChargeFormProps> = ({
  open,
  onClose,
  onSubmit,
}) => {
  const initialState: ServiceChargeCreate = {
    content_type_id: null,
    external_service_resource_id: null,
    feature_id: null,
    action_event: "",
    is_credit_charge: true,
    credit_charge: 0,
    unit_type: "ACTION",
    unit: null,
    unit_value: null,
    status: "ACTIVE",
    negative_deduction_allowed: true,
  };

  const [values, setValues] = useState<ServiceChargeCreate>(initialState);
  const [errors, setErrors] = useState<
    Partial<Record<keyof ServiceChargeCreate, string>>
  >({});
  const CreateServiceChargeResponse = useSelector(
    (state: any) => state.CreateServiceChargeDara
  );

  useEffect(() => {
    if (
      CreateServiceChargeResponse?.status &&
      CreateServiceChargeResponse?.data
    ) {
      toast.success(CreateServiceChargeResponse?.data?.data?.message);
      setValues(initialState);
      onClose();
    }
  }, [CreateServiceChargeResponse]);

  const handleChange = (e: any) => {
    const { name, value, type, checked } = e.target;
    let newValue: any;

    if (type === "checkbox") {
      newValue = checked;
    } else if (type === "number") {
      newValue = Number(value);
    } else {
      newValue =
        name === "action_event" && typeof value === "string"
          ? value.toUpperCase()
          : value;
    }

    setValues((prev) => ({
      ...prev,
      [name!]: newValue,
    }));
  };

  const handleSubmit = async () => {
    try {
      const valid = await validationSchema.validate(values, {
        abortEarly: false,
      });
      setErrors({});

      const payload: ServiceChargeCreate = {
        action_event: valid.action_event,
        feature_id: valid.feature_id ?? null,
        content_type_id: valid.content_type_id ?? null,
        external_service_resource_id:
          valid.external_service_resource_id ?? null,
        is_credit_charge: valid.is_credit_charge,
        credit_charge: valid.credit_charge,
        unit_type: valid.unit_type,
        unit: (valid.unit as ServiceChargeCreate["unit"]) ?? null,
        unit_value: valid.unit_value ?? null,
        status: valid.status,
        negative_deduction_allowed: valid.negative_deduction_allowed,
      };

      onSubmit(payload);
      setValues(initialState);
    } catch (err: any) {
      if (err.inner) {
        const formErrors: any = {};
        err.inner.forEach((validationError: any) => {
          formErrors[validationError.path as keyof ServiceChargeCreate] =
            validationError.message;
        });
        setErrors(formErrors);
      }
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create Service Charge</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField
            name="action_event"
            label="Action Event"
            value={values.action_event}
            onChange={handleChange}
            error={Boolean(errors.action_event)}
            helperText={errors.action_event}
            fullWidth
          />
          <TextField
            name="feature_id"
            label="Feature ID"
            type="number"
            inputProps={{ min: 1 }}
            value={values.feature_id ?? ""}
            onChange={handleChange}
            error={Boolean(errors.feature_id)}
            helperText={errors.feature_id}
            fullWidth
          />
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              name="content_type_id"
              label="Content Type ID"
              type="number"
              inputProps={{ min: 1 }}
              value={values.content_type_id ?? ""}
              onChange={handleChange}
              error={Boolean(errors.content_type_id)}
              helperText={errors.content_type_id}
              fullWidth
            />
            <TextField
              name="external_service_resource_id"
              label="External Resource ID"
              type="number"
              inputProps={{ min: 1 }}
              value={values.external_service_resource_id ?? ""}
              onChange={handleChange}
              error={Boolean(errors.external_service_resource_id)}
              helperText={errors.external_service_resource_id}
              fullWidth
            />
          </Box>
          <FormControl fullWidth>
            <InputLabel id="unit-type-label">Unit Type</InputLabel>
            <Select
              labelId="unit-type-label"
              name="unit_type"
              value={values.unit_type}
              onChange={handleChange}
              error={Boolean(errors.unit_type)}
              label="Unit Type"
            >
              <MenuItem value="ACTION">ACTION</MenuItem>
              <MenuItem value="TIME">TIME</MenuItem>
            </Select>
            {errors.unit_type && (
              <Box sx={{ color: "error.main" }}>{errors.unit_type}</Box>
            )}
          </FormControl>
          {values.unit_type === "TIME" && (
            <Box sx={{ display: "flex", gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel id="unit-label">Unit</InputLabel>
                <Select
                  labelId="unit-label"
                  name="unit"
                  value={values.unit || ""}
                  onChange={handleChange}
                  error={Boolean(errors.unit)}
                  label="Unit"
                >
                  <MenuItem value="SECOND">SECOND</MenuItem>
                  <MenuItem value="MINUTE">MINUTE</MenuItem>
                  <MenuItem value="HOUR">HOUR</MenuItem>
                  <MenuItem value="DAY">DAY</MenuItem>
                  <MenuItem value="MONTH">MONTH</MenuItem>
                  <MenuItem value="ANNUAL">ANNUAL</MenuItem>
                </Select>
                {errors.unit && (
                  <Box sx={{ color: "error.main" }}>{errors.unit}</Box>
                )}
              </FormControl>
              <TextField
                name="unit_value"
                label="Unit Value"
                type="number"
                inputProps={{ min: 0 }}
                value={values.unit_value ?? ""}
                onChange={handleChange}
                error={Boolean(errors.unit_value)}
                helperText={errors.unit_value}
                fullWidth
              />
            </Box>
          )}
          <FormControlLabel
            control={
              <Switch
                checked={values.is_credit_charge}
                onChange={handleChange}
                name="is_credit_charge"
                color="primary"
              />
            }
            label="Credit Charge?"
          />
          <TextField
            name="credit_charge"
            label="Credit Charge"
            type="number"
            inputProps={{ min: 0 }}
            value={values.credit_charge}
            onChange={handleChange}
            error={Boolean(errors.credit_charge)}
            helperText={errors.credit_charge}
            fullWidth
          />
          <FormControl fullWidth>
            <InputLabel id="status-label">Status</InputLabel>
            <Select
              labelId="status-label"
              name="status"
              value={values.status}
              onChange={handleChange}
              error={Boolean(errors.status)}
              label="Status"
            >
              <MenuItem value="ACTIVE">Active</MenuItem>
              <MenuItem value="UPCOMING">Upcoming</MenuItem>
            </Select>
            {errors.status && (
              <Box sx={{ color: "error.main" }}>{errors.status}</Box>
            )}
          </FormControl>
          <FormControlLabel
            control={
              <Switch
                checked={values.negative_deduction_allowed}
                onChange={handleChange}
                name="negative_deduction_allowed"
                color="primary"
              />
            }
            label="Negative Deduction Allowed"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" color="primary">
          Create Charge
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateServiceCharge;
